import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AuthTokens,
  LoginPayload,
  UserMembership,
  UserPrincipal,
} from '../types/data-models';

export interface LoginRequest {
  email: string;
  password: string;
  /** Optional tenant id when the user has multiple memberships. */
  tenantId?: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
  tenantName: string;
}

export interface LoginResult {
  user: UserPrincipal;
  tokens: AuthTokens;
  memberships: UserMembership[];
}

function toPrincipal(p: LoginPayload['user']): UserPrincipal {
  return {
    id: p.userId,
    email: p.email,
    name: null,
    tenantId: p.tenantId,
    role: p.roles?.[0] ?? '',
    roles: p.roles ?? [],
  };
}

export const authService = {
  async login(payload: LoginRequest): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload>>('/auth/login', payload);
    const body = res.data?.data ?? (res.data as unknown as LoginPayload | undefined);
    if (!body || !body.accessToken) throw new Error('Invalid login response: missing accessToken');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: body.memberships ?? [],
    };
  },

  async register(payload: RegisterRequest): Promise<LoginResult> {
    const res = await api.post<
      OneEnvelope<{ user: LoginPayload['user']; tokens: LoginPayload; memberships: UserMembership[] }>
    >('/auth/register', payload);
    const body = res.data?.data;
    if (!body || !body.tokens?.accessToken) {
      throw new Error('Invalid register response: missing accessToken');
    }
    return {
      user: toPrincipal(body.user),
      tokens: {
        accessToken: body.tokens.accessToken,
        refreshToken: body.tokens.refreshToken,
      },
      memberships: body.memberships ?? [],
    };
  },

  /**
   * Exchange a Google ID token for our own tokens. The backoffice does not
   * self-serve: the backend rejects Google identities without an account, so
   * a `signup_required` handoff here means the client ids are misconfigured.
   */
  async loginWithGoogle(idToken: string): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload & { status?: string }>>('/auth/google', {
      idToken,
    });
    const body = res.data?.data;
    if (!body) throw new Error('Invalid Google sign-in response');
    if (body.status === 'signup_required') {
      throw new Error('This Google account does not have backoffice access');
    }
    if (!body.accessToken) throw new Error('Invalid Google sign-in response: missing accessToken');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: body.memberships ?? [],
    };
  },

  async switchTenant(tenantId: number): Promise<LoginResult> {
    const res = await api.post<OneEnvelope<LoginPayload>>('/auth/switch-tenant', { tenantId });
    const body = res.data?.data ?? (res.data as unknown as LoginPayload | undefined);
    if (!body || !body.accessToken) throw new Error('Invalid switch-tenant response');
    return {
      user: toPrincipal(body.user),
      tokens: { accessToken: body.accessToken, refreshToken: body.refreshToken },
      memberships: body.memberships ?? [],
    };
  },

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const res = await api.post<OneEnvelope<AuthTokens> | AuthTokens>('/auth/refresh', { refreshToken });
    const wrapped = res.data as OneEnvelope<AuthTokens>;
    return wrapped?.data ?? (res.data as AuthTokens);
  },
};
